import {
  WorkAssemblySession,
  type ImportedModelResult,
} from '@puppycad/cad-core'

/**
 * complete WorkAssembly 的 web 协议 Adapter。
 *
 * 该类只负责把 archive/package 身份、运行时上下文与加载交付物映射为 cad-core 快照；
 * 活动版本、并发构建和失败退役 owner 全部由 `WorkAssemblySession` 管理。
 */
export class CompleteWorkAssemblySession {
  /** cad-core 统一拥有活动版本、Registry 与失败退役快照。 */
  private readonly session = new WorkAssemblySession()

  /**
   * 创建 complete WorkAssembly 会话。
   *
   * @param options 实例关系释放边界。
   */
  public constructor(
    private readonly options: PuppyCADWeb.CompleteWorkAssemblySessionOptions,
  ) {}

  /**
   * 从加载单元解析不可变 WorkAssembly 版本键。
   *
   * 版本键描述当前项目 occurrence，而不是承载字节的归档文件。archiveFile 只参与包文件
   * 身份校验，防止把其它节点的归档误挂到当前 occurrence。
   *
   * @param source 当前节点与可选完整归档快照。
   * @returns complete 模式版本键；非 complete 或版本字段不完整时返回 null。
   */
  private resolveVersionKey(
    source: PuppyCADWeb.CompleteWorkAssemblyVersionSource,
  ): PuppyCAD.WorkAssemblyVersionKey | null {
    // 完整 GLB 同时承载零件与装配；只有装配根才拥有 WorkAssembly 文档与运行时绑定。
    // 在版本入口收口可保证零件继续使用 complete-glb-scene，而不会被迫伪造 _assembly.json。
    if (source.root.nodeType !== 'assembly') {
      return null
    }

    const completePackageFile = source.root.packageFiles.find(
      (file) => file.loadMode === 'complete-glb-scene',
    )
    if (!completePackageFile) {
      return null
    }

    const archiveFile = source.archiveFile
    const archiveUuid = archiveFile?.fileInfo.uuid.trim() ?? ''
    const packageUuid = completePackageFile.uuid.trim()
    const archiveMatchesPackage = Boolean(
      archiveFile
      && (archiveUuid && packageUuid
        ? archiveUuid === packageUuid
        : archiveFile.fileInfo.fileId === completePackageFile.fileId),
    )
    if (archiveFile && !archiveMatchesPackage) {
      throw new Error(
        'complete WorkAssembly archiveFile 与 complete 包文件身份不一致：'
        + `rootNodeKey=${source.root.nodeKey}, `
        + `packageFileId=${completePackageFile.fileId}, `
        + `archiveFileId=${archiveFile.fileInfo.fileId}`,
      )
    }

    const repositoryId = source.root.repositoryId.trim()
    const commitId = source.root.commitId.trim()

    if (!repositoryId || !commitId) {
      return null
    }

    return Object.freeze({ repositoryId, commitId })
  }

  /**
   * 在执行 Worker/场景提交前校验构建交付物属于 Registry 请求的版本。
   *
   * @param expectedVersionKey Registry 已归一化的目标版本。
   * @param input 尚未提交的完整 WorkAssembly 交付物。
   */
  private validateBuildDelivery(
    expectedVersionKey: PuppyCAD.WorkAssemblyVersionKey,
    input: PuppyCADWeb.CompleteWorkAssemblyPublishInput,
  ): void {
    const actualVersionKey = this.resolveVersionKey(input)
    if (!this.isSameVersionKey(expectedVersionKey, actualVersionKey)) {
      throw new Error(
        'complete WorkAssembly 构建交付版本不匹配：'
        + `expected=${expectedVersionKey.repositoryId}:${expectedVersionKey.commitId}, `
        + `rootNodeKey=${input.root.nodeKey}`,
      )
    }
    if (input.projectModelResult.runtimeState.rootNodeKey !== input.root.nodeKey) {
      throw new Error(
        'complete WorkAssembly 项目运行时状态根不一致：'
        + `rootNodeKey=${input.root.nodeKey}, `
        + `runtimeRootNodeKey=${input.projectModelResult.runtimeState.rootNodeKey}`,
      )
    }
  }

  /**
   * 查询仍由活动 Worker 运行时支撑的完整快照。
   *
   * @param source 当前加载请求的版本来源。
   * @returns 当前活动版本命中时返回快照，否则返回 null。
   */
  public getActiveSnapshot(
    source?: PuppyCADWeb.CompleteWorkAssemblyVersionSource,
  ): PuppyCAD.WorkAssemblySnapshot | null {
    if (!source) {
      return this.session.getActive()
    }
    const requestedVersionKey = this.resolveVersionKey(source)
    return requestedVersionKey ? this.session.getActive(requestedVersionKey) : null
  }

  /**
   * 读取当前活动快照，不重新解析外部加载请求。
   *
   * 该入口供版本切换成功后的资源差集清理使用；调用方只能读取快照拥有的结果引用，
   * 不能据此绕过 `getActiveSnapshot` 的请求版本校验。
   *
   * @returns 当前活动快照；尚未发布完整版本时返回 null。
   */
  public supportsRoot(source: PuppyCADWeb.CompleteWorkAssemblyVersionSource): boolean {
    return this.resolveVersionKey(source) !== null
  }

  /**
   * 解析 complete 根装配补充给项目运行时状态的 occurrence 上下文。
   *
   * 首次打开、历史版本切换和提交后重载必须共用该边界；否则同一份 complete
   * WorkAssembly 会因入口不同而得到不同的 nodeKey—Object3D 绑定。
   *
   * @param input 当前版本的装配文档、节点包与准备快照。
   * @returns complete 装配返回权威 occurrence 上下文；普通项目模型返回 undefined。
   */
  private resolveRuntimeContext(
    input: PuppyCADWeb.CompleteWorkAssemblyRuntimeContextInput,
  ): PuppyCAD.ProjectModelCompleteRuntimeStateContext | undefined {
    const versionKey = this.resolveVersionKey(input)
    if (!versionKey) {
      return undefined
    }

    const preparedSnapshot = input.preparedSnapshotsByNodeKey.get(input.root.nodeKey)
    if (!preparedSnapshot) {
      throw new Error(
        `complete WorkAssembly 缺少 prepared snapshot：rootNodeKey=${input.root.nodeKey}`,
      )
    }
    const assemblyDocument = input.projectModelResult.assemblyDocumentsByNodeKey.get(
      input.root.nodeKey,
    )
    if (!assemblyDocument) {
      throw new Error(
        `complete WorkAssembly 缺少根装配文档：rootNodeKey=${input.root.nodeKey}`,
      )
    }
    const completePackageResults = input.packageResults.filter(packageResult => (
      packageResult.nodeKey === input.root.nodeKey
      && packageResult.file.loadMode === 'complete-glb-scene'
      && packageResult.file.fileId === preparedSnapshot.packageFile.fileId
    ))
    if (completePackageResults.length !== 1) {
      throw new Error(
        'complete WorkAssembly 根包无法唯一定位：'
        + `rootNodeKey=${input.root.nodeKey}, `
        + `packageFileId=${preparedSnapshot.packageFile.fileId}, `
        + `candidates=${completePackageResults.length}`,
      )
    }

    return Object.freeze({
      projectNodeKey: input.root.nodeKey,
      projectRoot: input.projectModelResult.root,
      packageRootNodeKeys: new Set(input.packageResults.map(packageResult => packageResult.nodeKey)),
      assemblyDocument,
      completePackageResult: completePackageResults[0]!,
      occurrenceGraph: preparedSnapshot.occurrenceGraph,
    })
  }

  /**
   * 原子发布一个已经完成 Worker 与场景提交的 WorkAssembly，并淘汰旧活动版本。
   *
   * @param input 当前版本的完整项目模型交付物。
   * @returns 新活动快照及旧版本是否被替换。
   */
  /**
   * 在版本注册表边界内解析或执行完整 WorkAssembly 构建事务。
   *
   * 同版本的 prepare、下载、解析、运行时提交必须全部放在 builder 内；注册表命中或已有
   * in-flight 构建时不会调用 builder。新快照成功发布后，才淘汰此前活动版本。
   *
   * @param source 当前项目 occurrence 与归档文件身份。
   * @param builder Registry 未命中时执行的完整构建事务。
   * @returns 已成为活动版本的快照及替换信息。
   */
  public async resolveRoot(
    source: PuppyCADWeb.CompleteWorkAssemblyVersionSource,
    builder: PuppyCADWeb.CompleteWorkAssemblyBuilder,
  ): Promise<PuppyCADWeb.CompleteWorkAssemblyPublishResult> {
    const versionKey = this.resolveVersionKey(source)
    if (!versionKey) {
      throw new Error(`complete WorkAssembly 缺少有效版本身份：rootNodeKey=${source.root.nodeKey}`)
    }

    const resolved = await this.session.resolve(
      versionKey,
      async (normalizedVersionKey) => {
        const buildContext: PuppyCADWeb.CompleteWorkAssemblyBuildContext = Object.freeze({
          validateDelivery: (input): void => {
            this.validateBuildDelivery(normalizedVersionKey, input)
          },
          resolveRuntimeContext: (input) => {
            const runtimeContext = this.resolveRuntimeContext(input)
            if (!runtimeContext) {
              throw new Error(
                `complete WorkAssembly 构建上下文无法解析普通项目根：rootNodeKey=${input.root.nodeKey}`,
              )
            }
            return runtimeContext
          },
        })
        const input = await builder(buildContext)
        this.validateBuildDelivery(normalizedVersionKey, input)
        return this.createSnapshot(normalizedVersionKey, input)
      },
    )
    if (resolved.retirementErrors.length > 0) {
      throw new AggregateError(resolved.retirementErrors, 'WorkAssembly 旧版本资源释放失败')
    }
    return Object.freeze({
      snapshot: resolved.snapshot,
      replacedActiveSnapshot: resolved.replacedActiveSnapshot,
    })
  }

  /**
   * 把当前活动快照的所有权转交给已经提交的局部 revision，并重新发布不可变快照。
   *
   * 旧快照与新 revision 复用同一项目根和未替换 Object3D，因而这里禁止调用 `evict()`；
   * Registry 原子替换缓存槽位，新快照立即接管当前组合结果与包结果的后续释放责任。
   *
   * @param input 局部场景、Worker 与缓存全部提交后的完整项目交付物。
   * @returns 新活动 revision 快照。
   */
  public prepareRevision(
    input: PuppyCADWeb.CompleteWorkAssemblyPublishInput,
  ): PuppyCADWeb.PreparedCompleteWorkAssemblyRevision | null {
    if (!this.session.getActive()) {
      return null
    }
    const versionKey = this.resolveVersionKey(input)
    if (!versionKey) {
      return null
    }

    // 先完整构建并校验新快照，再由 Registry 一次性替换两个槽位；失败时旧活动快照保持不变。
    const nextSnapshot = this.createSnapshot(versionKey, input)
    let committed = false
    return Object.freeze({
      commit: (): PuppyCAD.WorkAssemblySnapshot => {
        if (committed) {
          throw new Error('complete WorkAssembly revision 不允许重复提交')
        }
        this.session.replaceActive(nextSnapshot)
        committed = true
        return nextSnapshot
      },
    })
  }

  /**
   * 失效当前活动 WorkAssembly。
   *
   * Viewer 或 Worker 根运行时被销毁时必须同步调用，防止后续恢复可见但不可编辑的旧场景。
   *
   * @returns 找到并淘汰活动快照时返回 true。
   */
  public invalidate(): boolean {
    return this.session.invalidate()
  }

  /** 销毁会话内仍保留的全部已发布快照。 */
  public dispose(): void {
    this.session.dispose()
  }

  /**
   * 构建不可变 WorkAssembly 快照。
   *
   * @param versionKey 已解析的仓库与提交版本。
   * @param input 当前版本完整交付物。
   * @returns 可交给 cad-core 注册表发布的快照。
   */
  private createSnapshot(
    versionKey: PuppyCAD.WorkAssemblyVersionKey,
    input: PuppyCADWeb.CompleteWorkAssemblyPublishInput,
  ): PuppyCAD.WorkAssemblySnapshot {
    this.validateBuildDelivery(versionKey, input)
    const pendingResults = new Set(this.collectUniqueImportedResults([
      input.importedResult,
      ...input.packageResults.map((packageResult) => packageResult.result),
    ]))

    return Object.freeze({
      versionKey,
      projectModelResult: input.projectModelResult,
      packageResults: Object.freeze([...input.packageResults]),
      importedResult: input.importedResult,
      dispose: (): void => {
        if (pendingResults.size === 0) {
          return
        }
        const cleanupErrors: unknown[] = []
        for (const result of [...pendingResults]) {
          try {
            this.options.disposeImportedResult(result, 'dispose WorkAssembly snapshot')
            pendingResults.delete(result)
          } catch (error) {
            // 单个结果失败不能阻断其他 package owner；失败项保留在集合中供幂等重试。
            cleanupErrors.push(error)
          }
        }
        if (cleanupErrors.length > 0) {
          throw new AggregateError(cleanupErrors, 'WorkAssembly 快照资源释放失败')
        }
      },
    })
  }

  /**
   * 按对象引用去重快照拥有的导入结果。
   *
   * @param results 组合结果与包结果。
   * @returns 保持首次出现顺序的唯一结果。
   */
  private collectUniqueImportedResults(
    results: readonly ImportedModelResult[],
  ): readonly ImportedModelResult[] {
    return [...new Set(results)]
  }

  /**
   * 判断两个版本键是否完全一致。
   *
   * @param left 左侧版本。
   * @param right 右侧版本。
   * @returns 仓库与提交均相同时返回 true。
   */
  private isSameVersionKey(
    left: PuppyCAD.WorkAssemblyVersionKey | null,
    right: PuppyCAD.WorkAssemblyVersionKey | null,
  ): boolean {
    return Boolean(
      left
      && right
      && left.repositoryId === right.repositoryId
      && left.commitId === right.commitId,
    )
  }
}
